import { useEffect, useMemo, useState } from "react";
import api, { assetUrl } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Check, Cube, UploadSimple } from "@phosphor-icons/react";

const ZONE_LABELS = {
  center: "Logo Central", left: "Logo Izquierdo", right: "Logo Derecho",
  topleft: "Logo Superior Izquierdo", topright: "Logo Superior Derecho",
  top: "Logo Superior", bottom: "Logo Inferior",
};

// Per-zone rules: which library logos are allowed in each predefined zone, and whether the
// client may upload their own logo there. The flat extra charge for a custom logo lives at
// product level (`customer_upload_price`).
export default function ZoneLogoRules({ parts, metals, onPartsChange, uploadPrice, onUploadPriceChange }) {
  const [logos, setLogos] = useState([]);
  useEffect(() => { api.get("/logos", { params: { only_3d: true } }).then((r) => setLogos(r.data || [])).catch(() => {}); }, []);
  const metalList = (metals || []).filter((m) => m && m.id);

  const zones = useMemo(() => {
    const isBase = (p) => (p.slot || "base") === "base" || p.role === "band";
    const logoParts = (parts || []).filter((p) => p.role === "logo" && !isBase(p));
    const bySlot = {};
    logoParts.forEach((p) => { (bySlot[p.slot || "logo"] = bySlot[p.slot || "logo"] || []).push(p); });
    return Object.entries(bySlot).map(([slot, arr], i) => {
      const tpl = arr.find((x) => x.slot_anchor) || arr[0];
      return { slot, tpl, label: ZONE_LABELS[slot] || `Logo ${i + 1}` };
    });
  }, [parts]);

  const patchAnchor = (anchorId, patch) => {
    onPartsChange((parts || []).map((pt) => (pt.id === anchorId ? { ...pt, ...patch } : pt)));
  };
  const toggleLogo = (zone, logoId) => {
    const cur = zone.tpl.allowed_logo_ids || [];
    const next = cur.includes(logoId) ? cur.filter((x) => x !== logoId) : [...cur, logoId];
    patchAnchor(zone.tpl.id, { allowed_logo_ids: next });
  };
  const toggleMetal = (zone, metalId) => {
    const cur = zone.tpl.allowed_metals || [];
    const next = cur.includes(metalId) ? cur.filter((x) => x !== metalId) : [...cur, metalId];
    patchAnchor(zone.tpl.id, { allowed_metals: next });
  };

  return (
    <div className="border border-white/10 rounded-xl p-5 bg-[#121212] space-y-5" data-testid="zone-logo-rules-card">
      <div>
        <h3 className="font-semibold flex items-center gap-2"><Cube size={16} className="text-gold" /> Logos por zona</h3>
        <p className="text-xs text-neutral-500 mt-1">Elige qué logos puede usar el cliente en cada zona predefinida del producto. Si no marcas ninguno, se permiten todos.</p>
      </div>

      {zones.length === 0 && <div className="text-xs text-neutral-500">Este producto aún no tiene zonas de logo. Crea piezas de logo en el editor 3D y márcalas como plantilla de zona.</div>}

      {zones.map((zone) => {
        const allowed = zone.tpl.allowed_logo_ids || [];
        return (
          <div key={zone.slot} className="border-t border-white/10 pt-4 first:border-t-0 first:pt-0" data-testid={`zone-rule-${zone.slot}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-sm">{zone.label}</div>
              <div className="text-[11px] text-neutral-500">{allowed.length ? `${allowed.length} permitidos` : "Todos permitidos"}</div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {logos.length === 0 && <div className="text-xs text-neutral-500 col-span-full">No tienes logos 3D todavía.</div>}
              {logos.map((l) => {
                const on = allowed.includes(l.id);
                return (
                  <button key={l.id} type="button" onClick={() => toggleLogo(zone, l.id)}
                    data-testid={`zone-${zone.slot}-logo-${l.id}`}
                    className={`relative rounded-lg border overflow-hidden text-left ${on ? "border-gold ring-2 ring-gold/30" : "border-white/10 hover:border-white/25"}`}>
                    {l.url || l.thumb_url
                      ? <img src={assetUrl(l.thumb_url || l.url)} alt={l.name} className="w-full h-12 object-contain bg-white/5" />
                      : <div className="w-full h-12 flex items-center justify-center bg-white/5 text-neutral-500"><Cube size={18} /></div>}
                    <div className="px-1 py-0.5 truncate text-[10px] text-neutral-300">{l.name}</div>
                    {on && <span className="absolute top-1 right-1 bg-gold text-obsidian rounded-full p-0.5"><Check size={9} weight="bold" /></span>}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center justify-between mt-3 cursor-pointer">
              <span className="text-xs text-neutral-400 flex items-center gap-1.5"><UploadSimple size={13} /> Permitir que el cliente suba su propio logo en esta zona</span>
              <Switch checked={!!zone.tpl.customer_upload_enabled} onCheckedChange={(v) => patchAnchor(zone.tpl.id, { customer_upload_enabled: v })} data-testid={`zone-${zone.slot}-upload-switch`} />
            </label>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-neutral-400">Colores que el cliente puede elegir para este logo</span>
                <span className="text-[11px] text-neutral-500">{(zone.tpl.allowed_metals || []).length ? `${(zone.tpl.allowed_metals || []).length} permitidos` : "Sigue el color de la pieza principal"}</span>
              </div>
              {metalList.length === 0 ? (
                <div className="text-[11px] text-neutral-500">Primero activa colores en la ficha del producto (sección Materiales).</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {metalList.map((m) => {
                    const on = (zone.tpl.allowed_metals || []).includes(m.id);
                    return (
                      <button key={m.id} type="button" onClick={() => toggleMetal(zone, m.id)}
                        data-testid={`zone-${zone.slot}-metal-${m.id}`}
                        className={`px-2.5 py-1 rounded-full border text-[11px] flex items-center gap-1.5 ${on ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-neutral-400 hover:border-white/30"}`}>
                        <span className="inline-block w-3 h-3 rounded-full border border-white/20" style={{ background: m.hex }} />
                        {m.name}{on && <Check size={11} weight="bold" />}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="text-[10px] text-neutral-500 mt-1.5">Si no marcas ninguno, el logo usará el mismo color que la pieza principal. Si marcas 1 o más, el cliente podrá elegir el color de este logo por separado.</p>
            </div>
          </div>
        );
      })}

      <div className="border-t border-white/10 pt-4">
        <div className="font-medium text-sm mb-1">Cargo adicional por logo personalizado</div>
        <div className="text-xs text-neutral-500 mb-2">Se cobra una sola vez por diseño si el cliente usa un logo propio en cualquier zona.</div>
        <div className="relative w-40">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">+$</span>
          <Input type="number" value={uploadPrice ?? 0} onChange={(e) => onUploadPriceChange(parseFloat(e.target.value) || 0)} className="pl-8 bg-white/5 border-white/15 h-9" data-testid="customer-upload-price-input" />
        </div>
      </div>
    </div>
  );
}
